import { useId } from 'react'
import {
  DISPLAY_LOCALES,
  FEED_UOM_SUGGESTIONS,
  MARKET_PRESETS,
  type FarmSettingsRecord,
  type MarketCode,
  type NewFarmInput,
} from '@henlytics/types'
import { NumericCell } from '../entry/NumericCell'

/** Form state. Every field stays a string while it is edited, like every other entry cell. */
export interface FarmSettingsDraft {
  name: string
  currencyCode: string
  timezone: string
  locale: string
  feedUom: string
  layerStartWeek: string
  expectedLayingWeeks: string
  expectedEggsPerHen: string
  expectedSpentHenSalvagePerBird: string
}

export function draftFromPreset(market: MarketCode): FarmSettingsDraft {
  const preset = MARKET_PRESETS[market]
  return {
    name: '',
    currencyCode: preset.currencyCode,
    timezone: preset.timezone,
    locale: preset.locale,
    feedUom: preset.feedUom,
    // The schema defaults. Breed guides differ, so the owner should check them.
    layerStartWeek: '19',
    expectedLayingWeeks: '52',
    expectedEggsPerHen: '300',
    expectedSpentHenSalvagePerBird: '',
  }
}

export function draftFromSettings(settings: FarmSettingsRecord): FarmSettingsDraft {
  return {
    name: settings.name,
    currencyCode: settings.currencyCode,
    timezone: settings.timezone,
    locale: settings.locale,
    feedUom: settings.feedUom,
    layerStartWeek: String(settings.layerStartWeek),
    expectedLayingWeeks: String(settings.expectedLayingWeeks),
    expectedEggsPerHen: String(settings.expectedEggsPerHen),
    expectedSpentHenSalvagePerBird: settings.expectedSpentHenSalvagePerBird ?? '',
  }
}

/** What the API receives. Week and egg counts become integers; the price stays a decimal string (§3.10). */
export function inputFromDraft(draft: FarmSettingsDraft): NewFarmInput {
  return {
    name: draft.name.trim(),
    currencyCode: draft.currencyCode,
    timezone: draft.timezone.trim(),
    locale: draft.locale,
    feedUom: draft.feedUom.trim(),
    layerStartWeek: Number.parseInt(draft.layerStartWeek, 10),
    expectedLayingWeeks: Number.parseInt(draft.expectedLayingWeeks, 10),
    expectedEggsPerHen: Number.parseInt(draft.expectedEggsPerHen, 10),
    expectedSpentHenSalvagePerBird:
      draft.expectedSpentHenSalvagePerBird === '' ? null : draft.expectedSpentHenSalvagePerBird,
  }
}

const TIME_ZONES = Intl.supportedValuesOf('timeZone')
const CURRENCIES = Intl.supportedValuesOf('currency')

function currencyNames(locale: string): Intl.DisplayNames | null {
  try {
    return new Intl.DisplayNames([locale], { type: 'currency' })
  } catch {
    return null
  }
}

/** A fixed sample amount in the chosen format. Display only, never a farm figure. */
function formatSample(locale: string, currency: string): string {
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(123456.5)
  } catch {
    return '—'
  }
}

export interface FarmSettingsFieldsProps {
  value: FarmSettingsDraft
  onChange: (patch: Partial<FarmSettingsDraft>) => void
  /** Money has been recorded, so the currency can no longer change. */
  currencyLocked?: boolean
  disabled?: boolean
}

/**
 * The farm's settings as entry cells, shared by the setup screen and the
 * Settings page. Values arrive by props; saving is the page's job (§5).
 */
export function FarmSettingsFields({
  value,
  onChange,
  currencyLocked = false,
  disabled = false,
}: FarmSettingsFieldsProps) {
  const timeZoneListId = useId()
  const feedUomListId = useId()
  const names = currencyNames(value.locale)

  return (
    <>
      <label className="cell wide">
        <span className="cell-label">Farm name</span>
        <input
          className="cell-input"
          value={value.name}
          onChange={(event) => onChange({ name: event.target.value })}
          disabled={disabled}
          required
          maxLength={120}
        />
      </label>

      <label className="cell">
        <span className="cell-label">Currency</span>
        <select
          className="cell-input"
          value={value.currencyCode}
          onChange={(event) => onChange({ currencyCode: event.target.value })}
          disabled={disabled || currencyLocked}
        >
          {CURRENCIES.map((code) => (
            <option key={code} value={code}>
              {code} — {names?.of(code) ?? code}
            </option>
          ))}
        </select>
        <span className="cell-hint">
          {currencyLocked
            ? `Fixed: money is already recorded in ${value.currencyCode}.`
            : 'Cannot be changed once money is recorded.'}
        </span>
      </label>

      <label className="cell">
        <span className="cell-label">Number format</span>
        <select
          className="cell-input"
          value={value.locale}
          onChange={(event) => onChange({ locale: event.target.value })}
          disabled={disabled}
        >
          {DISPLAY_LOCALES.map((locale) => (
            <option key={locale.code} value={locale.code}>
              {locale.label}
            </option>
          ))}
        </select>
        <span className="cell-hint">Shown as {formatSample(value.locale, value.currencyCode)}</span>
      </label>

      <label className="cell">
        <span className="cell-label">Timezone</span>
        <input
          className="cell-input"
          list={timeZoneListId}
          value={value.timezone}
          onChange={(event) => onChange({ timezone: event.target.value })}
          disabled={disabled}
          required
        />
        <datalist id={timeZoneListId}>
          {TIME_ZONES.map((zone) => (
            <option key={zone} value={zone} />
          ))}
        </datalist>
        <span className="cell-hint">Decides which day &ldquo;today&rdquo; is.</span>
      </label>

      <label className="cell">
        <span className="cell-label">Feed unit</span>
        <input
          className="cell-input"
          list={feedUomListId}
          value={value.feedUom}
          onChange={(event) => onChange({ feedUom: event.target.value })}
          disabled={disabled}
          required
          maxLength={16}
        />
        <datalist id={feedUomListId}>
          {FEED_UOM_SUGGESTIONS.map((unit) => (
            <option key={unit} value={unit} />
          ))}
        </datalist>
        <span className="cell-hint">Default unit for new feed materials.</span>
      </label>

      <NumericCell
        label="Spent-hen price, per hen"
        value={value.expectedSpentHenSalvagePerBird}
        onChange={(next) => onChange({ expectedSpentHenSalvagePerBird: next })}
        disabled={disabled}
        placeholder="Not set"
        hint="What one hen sells for at the end of lay. Needed for loaded cost per egg."
      />
      <NumericCell
        label="Layer start week"
        integer
        value={value.layerStartWeek}
        onChange={(next) => onChange({ layerStartWeek: next })}
        disabled={disabled}
        hint="Weeks from hatch to first lay."
      />
      <NumericCell
        label="Laying weeks"
        integer
        value={value.expectedLayingWeeks}
        onChange={(next) => onChange({ expectedLayingWeeks: next })}
        disabled={disabled}
        hint="Expected length of lay."
      />
      <NumericCell
        label="Eggs per hen"
        integer
        value={value.expectedEggsPerHen}
        onChange={(next) => onChange({ expectedEggsPerHen: next })}
        disabled={disabled}
        hint="Lifetime, from your breed guide. 300 is a placeholder."
      />
    </>
  )
}
